import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ChevronRight as ChevronIcon,
  AccountBalanceOutlined as BankIcon,
  CalendarMonthOutlined as CalendarIcon,
  PaymentsOutlined as PaymentsIcon,
  ReceiptLongOutlined as ReceiptIcon,
  GroupsOutlined as GroupsIcon,
  FileDownloadOutlined as DownloadIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import api from '../../utils/functions/api';
import EmployeeName from '../../components/EmployeeName';
import { showAlert } from '../../utils/functions/globalDialog';
import { getBankCode, getBankLabel } from '../../constants/thaiBanks';

const API = process.env.REACT_APP_API_URL;

const fmt = (n) =>
  new Intl.NumberFormat('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n || 0);
const fmtDate = (d) => (d ? dayjs(d).format('D MMM BB') : '-');

// ชื่องวด — ดูจากวันเริ่มงวด (ไม่ใช้วันสิ้นสุด เพราะเก็บเป็น 23:59:59.999Z แปลงเป็นเวลาไทยแล้วข้ามวัน)
const runTitle = (run) => {
  const month = dayjs(run.periodStart).format('MMMM BB');
  if (run.empType === 'FULLTIME') {
    return `รายเดือน · ${month}`;
  }

  return dayjs(run.periodStart).date() <= 15
    ? `รายวัน งวดที่ 1 (1-15) · ${month}`
    : `รายวัน งวดที่ 2 (16-สิ้นเดือน) · ${month}`;
};

const runKey = (run) => `${run.empType}|${run.periodStart}`;

const queryOf = (run) => ({
  periodStart: run.periodStart,
  periodEnd: run.periodEnd,
  empType: run.empType,
});

const bankText = (bank) => {
  if (!bank?.number) {
    return 'ยังไม่มีข้อมูลบัญชี';
  }

  return `${getBankLabel(bank.bank_name) || 'ไม่ระบุธนาคาร'} ${bank.number}`;
};

const TABLE_HEAD_SX = {
  '& th': {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: 'text.secondary',
    bgcolor: (theme) => alpha(theme.palette.text.primary, 0.04),
    whiteSpace: 'nowrap',
  },
};

// ป้ายสรุปตัวเลข (ใช้ทั้งหน้ารายการและใน dialog)
function StatTile({ label, value, color = 'primary', sub }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: 140,
        p: 1.75,
        borderRadius: 3,
        borderColor: (theme) => alpha(theme.palette[color].main, 0.35),
        bgcolor: (theme) => alpha(theme.palette[color].main, 0.06),
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
      <Typography
        variant="h6"
        fontWeight={800}
        sx={{ color: `${color}.main`, lineHeight: 1.3 }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

function SectionTitle({ icon, children, aside }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', color: 'primary.main' }}>{icon}</Box>
      <Typography fontWeight={800} sx={{ flex: 1 }}>
        {children}
      </Typography>
      {aside}
    </Stack>
  );
}

// ข้อมูลผู้โอน (บริษัท) ในหัวไฟล์โอนเงินธนาคาร — คัดลอกจากไฟล์ตัวอย่างของ HR ทุกค่า
// BATCH_TYPE / REF_CODE / MODE_FLAG ไม่ทราบความหมายที่แน่ชัด ใช้ค่าเดียวกับตัวอย่างเป๊ะ
const BANK_FILE = {
  senderId: 'SOTUS',
  senderNameTh: 'บริษัท โซตัส เอ็นจิเนียริ่ง จำกัด ',
  senderNameEn: 'SOTUS ENGINEERING  co.,Ltd.',
  address: ['2244 ถนนปุณกัณฑ์', 'สงขลา', 'คอหงส์', 'หาดใหญ่', '90110'],
  taxId: '010557086951',
  senderBankCode: '006',
  debitAccount: '9156040792',
  batchType: '3',
  modeFlag: 'C',
  refCode: 'PLKBA01848',
};

const digitsOnly = (v) => String(v || '').replace(/\D/g, '');
const money = (n) => Math.round((n || 0) * 100) / 100;

// สร้างแถวไฟล์โอนเงินธนาคาร (ไม่มีหัวตาราง) ตามไฟล์ตัวอย่างของ HR:
//  แถวแรก = หัวไฟล์ (H) / แถวถัดไป = รายการโอน (D) 1 แถวต่อ 1 บัญชีปลายทาง (ยอดรวมของคนที่โอนมาให้ด้วย)
//  27 คอลัมน์ทุกแถว — เลขที่ขึ้นต้นด้วยศูนย์เก็บเป็นข้อความ
// คืน { rows, problems } — problems = รายชื่อที่ข้อมูลไม่พอสร้างไฟล์ (ไม่มีเลขบัญชี/รหัสธนาคาร)
const buildBankFileRows = (employees, run) => {
  const byId = new Map(employees.map((r) => [r.employeeId, r]));
  const isMember = (r) => r.payTo && byId.has(r.payTo);

  // ยอดโอนต่อผู้รับ เรียงตามลำดับพนักงาน
  const targets = new Map();
  employees
    .filter((r) => !isMember(r))
    .forEach((r) => targets.set(r.employeeId, 0));
  employees.forEach((r) => {
    const targetId = isMember(r) ? r.payTo : r.employeeId;
    targets.set(targetId, (targets.get(targetId) || 0) + (r.total || 0));
  });

  const date = dayjs(run.dueDate).format('DDMMYYYY');
  const problems = [];
  const details = [];
  [...targets.entries()]
    .filter(([, amount]) => amount > 0)
    .forEach(([id, amount]) => {
      const target = byId.get(id);
      const bankName = target?.bank?.bank_name;
      const account = digitsOnly(target?.bank?.number);
      const bankCode = getBankCode(bankName);
      if (!account || !bankCode) {
        problems.push(
          `${target?.name || id}: ${
            !account
              ? 'ไม่มีเลขบัญชี'
              : `ไม่ทราบรหัสธนาคาร (${bankName || 'ไม่ระบุธนาคาร'})`
          }`,
        );
      }
      details.push({
        name: `${target?.title || ''}${target?.name || ''}`,
        account,
        bankCode,
        amount: money(amount),
      });
    });

  const total = money(details.reduce((acc, d) => acc + d.amount, 0));
  const header = [
    '10',
    'H',
    '0001',
    BANK_FILE.batchType,
    '',
    BANK_FILE.senderId,
    BANK_FILE.senderNameTh,
    BANK_FILE.senderNameEn,
    ...BANK_FILE.address,
    BANK_FILE.taxId,
    '',
    BANK_FILE.senderBankCode,
    date,
    BANK_FILE.modeFlag,
    BANK_FILE.refCode,
    details.length,
    total,
    '',
    '',
    '',
    '',
    '',
    '',
  ];
  const rows = details.map((d, i) => [
    '10',
    'D',
    '0001',
    i + 1,
    'DCR',
    d.bankCode,
    '',
    '',
    d.account,
    d.name,
    '',
    '',
    '',
    '',
    '',
    'N',
    BANK_FILE.debitAccount,
    date,
    '02',
    'OUR',
    '',
    '',
    '',
    '',
    '',
    '',
    d.amount,
  ]);

  return { rows: [header, ...rows], problems };
};

function RunDetail({ run, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [detail, setDetail] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`${API}/payroll-payment/detail`, {
      params: queryOf(run),
    });
    setDetail(data);
  }, [run]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePayTo = async (employee, payTo) => {
    setSavingId(employee);
    try {
      await api.put(`${API}/payroll-payment/payee`, {
        ...queryOf(run),
        employee,
        payTo,
      });
      await load();
    } finally {
      setSavingId(null);
    }
  };

  const byId = useMemo(
    () => new Map((detail?.rows || []).map((r) => [r.employeeId, r])),
    [detail],
  );

  // สรุปยอดโอนต่อบัญชีปลายทาง — รวมยอดของคนที่ให้โอนเข้าบัญชีคนอื่นเข้าไปด้วย
  const accounts = useMemo(() => {
    const map = new Map();
    (detail?.rows || []).forEach((r) => {
      if (r.total <= 0) {
        return;
      }
      const target = byId.get(r.payTo) || r;
      const entry = map.get(target.employeeId) || {
        target,
        amount: 0,
        members: [],
      };
      entry.amount += r.total;
      entry.members.push(r.name);
      map.set(target.employeeId, entry);
    });

    return [...map.values()].sort((a, b) =>
      a.target.name.localeCompare(b.target.name, 'th'),);
  }, [detail, byId]);

  const diff = detail ? detail.salaryTotal - detail.expensesTotal : 0;
  const transferTotal = accounts.reduce((s, a) => s + a.amount, 0);
  const mismatch = Math.abs(diff) > 1;

  const handleExport = () => {
    const { rows, problems } = buildBankFileRows(detail.rows, run);
    if (problems.length > 0) {
      showAlert(
        `สร้างไฟล์โอนเงินไม่ได้ — ข้อมูลไม่ครบ:\n${problems.join('\n')}`,
      );

      return;
    }
    const sheet = XLSX.utils.aoa_to_sheet(rows);
    // ยอดเงินแสดง 2 ตำแหน่งเหมือนตัวอย่าง (เช่น 19125.00) — หัวไฟล์คอลัมน์ U / รายการคอลัมน์ AA
    rows.forEach((cells, r) => {
      const amountCol = r === 0 ? 20 : 26;
      const cell = sheet[XLSX.utils.encode_cell({ r, c: amountCol })];
      if (cell && cell.t === 'n') {
        cell.z = '0.00';
      }
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'โอนเงินเดือน');
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([buffer], { type: 'application/octet-stream' }),
      `โอนเงินเดือน_${dayjs(run.periodStart).format('YYYY-MM')}_${
        run.empType
      }.xlsx`,
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 4,
          // กว้างพอให้ชื่อ/โครงการ/บัญชีในตารางไม่ตัดบรรทัด (แต่ละรายการเหลือ 2 บรรทัด)
          width: isMobile ? '100%' : 'min(1800px, 96vw)',
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          background: (t) =>
            `linear-gradient(135deg, ${alpha(
              t.palette.primary.main,
              0.14,
            )}, ${alpha(t.palette.primary.main, 0.03)})`,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            flexShrink: 0,
          }}
        >
          <PaymentsIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={800} noWrap>
            {runTitle(run)}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ color: 'text.secondary' }}
          >
            <CalendarIcon sx={{ fontSize: 15 }} />
            <Typography variant="caption">
              กำหนดจ่าย {fmtDate(run.dueDate)}
            </Typography>
          </Stack>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={!detail || detail.rows.length === 0}
          sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
        >
          Export Excel
        </Button>
        <IconButton onClick={onClose} aria-label="ปิด">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 2.5 }}>
        {!detail ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            {/* สรุปยอดภาพรวม */}
            <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap">
              <StatTile
                label="รวมรายการเบิก"
                value={fmt(detail.expensesTotal)}
              />
              <StatTile
                label="ยอดเงินเดือนรายคน"
                value={fmt(detail.salaryTotal)}
                color="info"
              />
              <StatTile
                label="ส่วนต่าง"
                value={fmt(diff)}
                color={mismatch ? 'warning' : 'success'}
                sub={mismatch ? 'ตรวจสอบก่อนโอน' : 'ตรงกัน'}
              />
            </Stack>

            {detail.rows.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                งวดนี้ยังไม่มียอดค่าแรงรายคน
              </Alert>
            ) : (
              <Stack spacing={1.25}>
                {!detail.salaryListId && (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    งวดนี้ยังไม่ได้กดบันทึกเงินเดือน —
                    ยอดรายคนคำนวณสดจากข้อมูลล่าสุด อาจเปลี่ยนได้จนกว่าจะบันทึก
                  </Alert>
                )}
                {mismatch && (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    ยอดเงินเดือนรายคนรวม {fmt(detail.salaryTotal)}{' '}
                    ไม่ตรงกับรายการเบิก {fmt(detail.expensesTotal)} (ต่าง{' '}
                    {fmt(diff)}) — ตรวจสอบก่อนโอน
                  </Alert>
                )}
              </Stack>
            )}

            {/* รายการเบิก + รายคนที่ต้องโอน อยู่แถวเดียวกัน 2 คอลัมน์ (จอแคบเรียงซ้อน) */}
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                alignItems: 'start',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <SectionTitle icon={<ReceiptIcon />}>
                  รายการเบิกค่าแรงของงวดนี้
                </SectionTitle>
                <Paper
                  variant="outlined"
                  sx={{ overflowX: 'auto', borderRadius: 3 }}
                >
                  <Table size="small">
                    <TableHead sx={TABLE_HEAD_SX}>
                      <TableRow>
                        <TableCell>เลขที่</TableCell>
                        <TableCell>รายการ / โครงการ</TableCell>
                        <TableCell>สถานะ</TableCell>
                        <TableCell align="right">ยอด</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.expenses.map((e) => (
                        <TableRow key={e._id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {e.code}
                          </TableCell>
                          <TableCell sx={{ width: '100%', maxWidth: 0 }}>
                            <Typography variant="body2" noWrap title={e.name}>
                              {e.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              title={`${e.project?.project_number || ''} ${
                                e.project?.name || ''
                              }`}
                              sx={{ display: 'block' }}
                            >
                              {e.project?.project_number} {e.project?.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              variant={
                                e.status === 'SUCCESS' ? 'filled' : 'outlined'
                              }
                              label={
                                e.status === 'SUCCESS' ? 'จ่ายแล้ว' : e.status
                              }
                              color={
                                e.status === 'SUCCESS' ? 'success' : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {fmt(e.price)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {detail.expenses.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            align="center"
                            sx={{ py: 3, color: 'text.secondary' }}
                          >
                            ไม่มีรายการเบิก
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ mt: 1, px: 0.5 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    รวมรายการเบิก (ไม่รวมยกเลิก/ไม่ผ่าน)
                  </Typography>
                  <Typography variant="body2" fontWeight={800}>
                    {fmt(detail.expensesTotal)}
                  </Typography>
                </Stack>
              </Box>

              {detail.rows.length > 0 && (
                <Box sx={{ minWidth: 0 }}>
                  <SectionTitle
                    icon={<GroupsIcon />}
                    aside={
                      <Typography variant="caption" color="text.secondary">
                        ระบบจำการเลือกบัญชีไว้ใช้ทุกงวด
                      </Typography>
                    }
                  >
                    รายคนที่ต้องโอน
                  </SectionTitle>
                  <Paper
                    variant="outlined"
                    sx={{ overflowX: 'auto', borderRadius: 3 }}
                  >
                    <Table size="small">
                      <TableHead sx={TABLE_HEAD_SX}>
                        <TableRow>
                          <TableCell>พนักงาน</TableCell>
                          <TableCell align="right">ยอดโอน</TableCell>
                          <TableCell sx={{ minWidth: 220 }}>
                            โอนเข้าบัญชี
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.rows.map((r) => {
                          const target = byId.get(r.payTo) || r;

                          return (
                            <TableRow
                              key={r.employeeId}
                              hover
                              sx={r.total <= 0 ? { opacity: 0.5 } : undefined}
                            >
                              <TableCell sx={{ maxWidth: 200 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  noWrap
                                >
                                  <EmployeeName
                                    id={r.employeeId}
                                    name={r.name}
                                    onSaved={load}
                                  />
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  sx={{ display: 'block' }}
                                >
                                  {r.employeeCode}
                                  {r.splitExpense && ' · แยกรายการเบิกเฉพาะคน'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {fmt(r.total)}
                              </TableCell>
                              <TableCell>
                                {r.total > 0 ? (
                                  <>
                                    <Select
                                      size="small"
                                      fullWidth
                                      value={r.payTo || ''}
                                      displayEmpty
                                      disabled={savingId === r.employeeId}
                                      onChange={(e) =>
                                        handlePayTo(
                                          r.employeeId,
                                          e.target.value,
                                        )
                                      }
                                      sx={{
                                        borderRadius: 2,
                                        fontSize: '0.8rem',
                                        '& .MuiSelect-select': { py: 0.5 },
                                      }}
                                    >
                                      <MenuItem value="">บัญชีตัวเอง</MenuItem>
                                      {detail.rows
                                        .filter(
                                          (o) => o.employeeId !== r.employeeId,
                                        )
                                        .map((o) => (
                                          <MenuItem
                                            key={o.employeeId}
                                            value={o.employeeId}
                                          >
                                            บัญชีของ {o.name}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                    <Typography
                                      variant="caption"
                                      color={
                                        target.bank?.number
                                          ? 'text.secondary'
                                          : 'error'
                                      }
                                      noWrap
                                      sx={{ display: 'block', mt: 0.25 }}
                                    >
                                      {bankText(target.bank)}
                                    </Typography>
                                  </>
                                ) : (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    ไม่มียอดที่ต้องโอน
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Paper>
                </Box>
              )}
            </Box>

            {detail.rows.length > 0 && (
              <Box>
                <SectionTitle icon={<BankIcon />}>
                  สรุปยอดโอนต่อบัญชี
                </SectionTitle>
                <Stack spacing={1}>
                  {accounts.map((a) => (
                    <Paper
                      key={a.target.employeeId}
                      variant="outlined"
                      sx={{
                        p: 1.75,
                        borderRadius: 3,
                        borderLeft: 4,
                        borderLeftColor: a.target.bank?.number
                          ? 'primary.main'
                          : 'error.main',
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={2}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={700}>
                            {a.target.bank?.account_name || a.target.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color={
                              a.target.bank?.number ? 'text.secondary' : 'error'
                            }
                          >
                            {bankText(a.target.bank)}
                          </Typography>
                          {a.members.length > 1 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              รวมยอดของ: {a.members.join(', ')}
                            </Typography>
                          )}
                        </Box>
                        <Typography
                          fontWeight={800}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {fmt(a.amount)}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    mt: 1.5,
                    px: 2,
                    py: 1.5,
                    borderRadius: 3,
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                  }}
                >
                  <Typography fontWeight={700}>รวมโอนทั้งหมด</Typography>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ color: 'primary.main' }}
                  >
                    {fmt(transferTotal)}
                  </Typography>
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RunCard({ run, onClick }) {
  const saved = Boolean(run.salaryListId);
  const allPaid = run.itemCount > 0 && run.paidCount >= run.itemCount;
  const progress =
    run.itemCount > 0 ? (run.paidCount / run.itemCount) * 100 : 0;
  const accent = saved ? 'success' : 'warning';

  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 2,
        borderRadius: 3,
        cursor: 'pointer',
        borderLeft: 5,
        borderLeftColor: `${accent}.main`,
        transition: 'transform 0.15s, box-shadow 0.15s',
        '&:hover': { transform: 'translateY(-1px)', boxShadow: 3 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
          >
            <Typography fontWeight={800}>{runTitle(run)}</Typography>
            <Chip
              size="small"
              label={saved ? 'บันทึกเงินเดือนแล้ว' : 'ยังไม่บันทึกเงินเดือน'}
              color={accent}
              variant={saved ? 'filled' : 'outlined'}
            />
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ mt: 0.5, color: 'text.secondary' }}
          >
            <CalendarIcon sx={{ fontSize: 15 }} />
            <Typography variant="caption">
              กำหนดจ่าย {fmtDate(run.dueDate)} · {run.itemCount} รายการเบิก
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              color={allPaid ? 'success' : 'primary'}
              sx={{ flex: 1, height: 6, borderRadius: 3 }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: 'nowrap' }}
            >
              จ่ายแล้ว {run.paidCount}/{run.itemCount}
            </Typography>
          </Stack>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary">
            ยอดรวม
          </Typography>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
            {fmt(run.total)}
          </Typography>
        </Box>
        <ChevronIcon color="action" />
      </Stack>
    </Paper>
  );
}

function RunColumn({ title, runs, onSelect }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={800} color="text.secondary">
          {title}
        </Typography>
        <Chip size="small" label={runs.length} />
      </Stack>
      <Stack spacing={1.25}>
        {runs.map((run) => (
          <RunCard key={runKey(run)} run={run} onClick={() => onSelect(run)} />
        ))}
        {runs.length === 0 && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 3,
              borderStyle: 'dashed',
              textAlign: 'center',
              color: 'text.disabled',
            }}
          >
            <Typography variant="body2">ไม่มีงวด</Typography>
          </Paper>
        )}
      </Stack>
    </Box>
  );
}

function MonthGroup({ month, onSelect }) {
  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          mb: 1.5,
          px: 2,
          py: 1,
          borderRadius: 3,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <CalendarIcon color="primary" />
          <Typography fontWeight={800}>{month.label}</Typography>
        </Stack>
        <Typography fontWeight={800} sx={{ color: 'primary.main' }}>
          {fmt(month.total)}
        </Typography>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          alignItems: 'start',
        }}
      >
        <RunColumn title="รายเดือน" runs={month.monthly} onSelect={onSelect} />
        <RunColumn title="รายวัน" runs={month.daily} onSelect={onSelect} />
      </Box>
    </Box>
  );
}

export default function HrPayrollPayment() {
  const [runs, setRuns] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get(`${API}/payroll-payment`).then(({ data }) => setRuns(data));
  }, []);

  // ซ่อนงวดที่ยอดรวม 0 — ไม่มีอะไรต้องโอน
  const visibleRuns = useMemo(
    () => (runs || []).filter((r) => (r.total || 0) !== 0),
    [runs],
  );

  const summary = useMemo(() => {
    const list = visibleRuns;

    return {
      count: list.length,
      total: list.reduce((s, r) => s + (r.total || 0), 0),
      unsaved: list.filter((r) => !r.salaryListId).length,
    };
  }, [visibleRuns]);

  // จัดกลุ่มตามเดือนของวันเริ่มงวด (ใหม่สุดขึ้นก่อน) แล้วแยกรายเดือน/รายวันเป็นคนละคอลัมน์
  const months = useMemo(() => {
    const map = new Map();
    [...visibleRuns]
      .sort(
        (a, b) =>
          dayjs(b.periodStart).valueOf() - dayjs(a.periodStart).valueOf(),
      )
      .forEach((run) => {
        const key = dayjs(run.periodStart).format('YYYY-MM');
        const entry = map.get(key) || {
          key,
          label: dayjs(run.periodStart).format('MMMM BBBB'),
          monthly: [],
          daily: [],
          total: 0,
        };
        entry[run.empType === 'FULLTIME' ? 'monthly' : 'daily'].push(run);
        entry.total += run.total || 0;
        map.set(key, entry);
      });

    return [...map.values()];
  }, [visibleRuns]);

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          <PaymentsIcon />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
            จ่ายเงินเดือน
          </Typography>
          <Typography variant="body2" color="text.secondary">
            สรุปแต่ละงวดจากรายการเบิกค่าแรง — กดเพื่อดูว่าต้องโอนให้ใครเท่าไหร่
            และเลือกบัญชีที่ใช้รับโอน
          </Typography>
        </Box>
      </Stack>

      {!runs ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Stack
            direction="row"
            spacing={1.25}
            useFlexGap
            flexWrap="wrap"
            sx={{ my: 2.5 }}
          >
            <StatTile label="จำนวนงวด" value={summary.count} />
            <StatTile
              label="ยอดรวมทุกงวด"
              value={fmt(summary.total)}
              color="info"
            />
            <StatTile
              label="งวดที่ยังไม่บันทึกเงินเดือน"
              value={summary.unsaved}
              color={summary.unsaved > 0 ? 'warning' : 'success'}
            />
          </Stack>

          <Stack spacing={3}>
            {months.map((m) => (
              <MonthGroup key={m.key} month={m} onSelect={setSelected} />
            ))}
            {visibleRuns.length === 0 && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                ยังไม่มีรายการเบิกค่าแรง
              </Alert>
            )}
          </Stack>
        </>
      )}

      {selected && (
        <RunDetail run={selected} onClose={() => setSelected(null)} />
      )}
    </Box>
  );
}
